import { useState } from "react";
import { InputNumber, Select } from "antd";
import { InboxOutlined } from "@ant-design/icons";
import type { GisTargetLayer } from "../gisApi";

/**
 * Cột trái MÀN C — vùng tải tệp, đứng yên suốt phiên.
 *
 * Chọn tệp hoặc kéo-thả CHÍNH LÀ hành động tải lên; không có nút "tải lên" riêng
 * (Spec S12.5.3 §7.2). Nút duy nhất ở thanh đáy là nút nạp.
 */

const TARGET_LAYER_LABELS: Record<GisTargetLayer, string> = {
  GRAVE_POSITION: "Vị trí thửa mộ liệt sĩ (GRAVE_POSITION)",
  SECTION: "Ranh giới Lô / Hàng / Phân khu (SECTION)",
  CEMETERY_BOUNDARY: "Ranh giới tổng thể nghĩa trang (CEMETERY_BOUNDARY)",
};

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export type GisImportSourceProps = Readonly<{
  file: File | null;
  disabled: boolean;
  targetLayer: GisTargetLayer;
  sourceSrid: number;
  onFileChange: (file: File | null) => void;
  onTargetLayerChange: (value: GisTargetLayer) => void;
  onSourceSridChange: (value: number) => void;
}>;

export function GisImportSource({
  file,
  disabled,
  targetLayer,
  sourceSrid,
  onFileChange,
  onTargetLayerChange,
  onSourceSridChange,
}: GisImportSourceProps): React.JSX.Element {
  const [dragging, setDragging] = useState(false);
  /**
   * Khối kỹ thuật GẬP và mặc định ĐÓNG — RULING D3 (Spec §6). `sourceSrid` là
   * `NEVER_USER_UI` theo IRM, chỉ mở ra khi người dùng chủ động xem.
   *
   * Tự render theo `techOpen` thay vì `Collapse` của antd: cần chắc chắn nội dung
   * KHÔNG có trong DOM khi đóng, chứ không phải chỉ bị che bằng CSS.
   */
  const [techOpen, setTechOpen] = useState(false);

  return (
    <div className="gis-import-source">
      <label
        htmlFor="gis-import-file"
        data-testid="gis-import-dropzone"
        className={
          dragging ? "gis-import-dropzone is-dragging" : "gis-import-dropzone"
        }
        onDragOver={(event) => {
          event.preventDefault();
          if (!disabled) setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(event) => {
          event.preventDefault();
          setDragging(false);
          if (disabled) return;
          const dropped = event.dataTransfer?.files?.[0];
          if (dropped) onFileChange(dropped);
        }}
      >
        <InboxOutlined className="gis-import-dropzone-icon" />
        <span className="gis-import-dropzone-title">Kéo tệp GeoJSON vào đây</span>
        <span className="gis-import-dropzone-hint">hoặc bấm để chọn tệp</span>
        <span className="gis-import-dropzone-formats">.geojson · .json</span>
      </label>
      <input
        id="gis-import-file"
        type="file"
        accept=".geojson,.json"
        data-testid="gis-file-input"
        className="gis-import-file-input"
        disabled={disabled}
        onChange={(event) => onFileChange(event.target.files?.[0] ?? null)}
      />

      {file ? (
        <div className="gis-import-file-card">
          <span className="gis-import-file-name">{file.name}</span>
          <span className="gis-import-file-size">{formatSize(file.size)}</span>
        </div>
      ) : null}

      <div className="gis-import-tech">
        <button
          type="button"
          className="gis-import-tech-toggle"
          aria-expanded={techOpen}
          onClick={() => setTechOpen((open) => !open)}
        >
          <span aria-hidden="true">{techOpen ? "▾" : "▸"}</span> Thông số kỹ thuật
        </button>
        {techOpen ? (
          <div className="gis-import-tech-body">
            <label className="gis-import-field">
              <span className="gis-import-field-label">Lớp đích</span>
              <Select
                value={targetLayer}
                disabled={disabled}
                onChange={onTargetLayerChange}
                options={(Object.keys(TARGET_LAYER_LABELS) as GisTargetLayer[]).map((value) => ({
                  value,
                  label: TARGET_LAYER_LABELS[value],
                }))}
              />
            </label>
            <label className="gis-import-field">
              <span className="gis-import-field-label">Hệ toạ độ nguồn (SRID)</span>
              <InputNumber
                value={sourceSrid}
                disabled={disabled}
                className="gis-import-srid-input"
                onChange={(value) => onSourceSridChange(value ?? 4326)}
              />
            </label>
            <p className="gis-import-tech-note">
              Mặc định 4326 (WGS 84), thứ tự trục lon,lat. Tệp VN-2000 khai đúng SRID
              nguồn, hệ thống tự chuyển đổi.
            </p>
          </div>
        ) : null}
      </div>

      {/*
        Guardrail §5.4 — thông tin ngăn quyết định sai KHÔNG được ẩn sau một bước.
        Hiện ngay từ trạng thái rỗng, trước cả khi người dùng chọn tệp.
      */}
      <p className="gis-import-safety">
        <span aria-hidden="true">ⓘ</span> Tệp nguồn không bị sửa. Dòng sai bị loại và
        cách ly, hệ thống không tự sửa.
      </p>
    </div>
  );
}
